import { addNotification } from "@iolabs/notifier";
import { DispatchAction } from "@iolabs/redux-utils";
import { useDispatch } from "react-redux";
import React from "react";
import {Grid, MenuItem, Paper, Typography} from "@material-ui/core";
import {IFormTemplate} from "../../../../Crudder/components/CrudDetail/CrudDetailForm";
import useStyles from "./styles";
import {useField, useForm, useFormState} from "react-final-form";
import {OfferResponse} from "../../../../Api/data/offer/types";
import CrudDetailAutoFields from "../../../../Crudder/components/CrudDetail/CrudDetailAutoFields";
import CompanyContactsFormPart from "./CompanyContactsFormPart";
import InputSelect from "../../../../Crudder/components/InputSelect";
import {getAllCurrencies} from "../../../../Api/data/currency/client";
import {CurrencyResponse} from "../../../../Api/data/currency/types";
import {lookupCompany} from "../../../../Api/data/company/client";
import {OnBlurValueChange} from "../../../../../utils/Form";

interface IProcessingFileProps {
    name: string,
    progress: number,
}

const CompanyForm: React.FC<IFormTemplate<OfferResponse>> = ({ data, itemFields}) => {
    const classes = useStyles();
    const dispatch = useDispatch<DispatchAction>();

    const form = useForm();
    const {submitting} = useFormState({
        subscription: {
            submitting: true,
        }
    });

    return (
        <>
            <Grid container spacing={2} className={classes.formContainer}>
                {/* Horní řada - 3 sloupce */}
                <Grid item md={4}>
                    <Paper className={classes.section}>
                        <Typography variant={"caption"}>Identifikace</Typography>
                        <CrudDetailAutoFields<OfferResponse>
                            form={form as any}
                            itemFields={itemFields}
                            submitting={submitting}
                            includeFields={["regNumber", "vatNumber", "name"]}
                        />
                        <OnBlurValueChange<string>
                            name="regNumber"
                            onChange={(val, prev) => {
                                console.log("regNumber změněn po blur:", prev, "→", val);
                                lookupCompany(val).then((result) => {
                                    form.batch(() => {
                                        form.change("name", result.name);
                                        form.change("regNumber", result.regNumber);
                                        form.change("vatNumber", result.vatNumber);
                                        form.change("address.line1", result.address?.line1);
                                        form.change("address.line2", result.address?.line2);
                                        form.change("address.city", result.address?.city);
                                        form.change("address.country", result.address?.country);
                                        form.change("address.zipCode", result.address?.zipCode);
                                    })
                                    console.log(result);
                                }).catch(e => {
                                    dispatch(
                                        addNotification({
                                            notification: {
                                                variant: "error",
                                                message: e.response?.data?.ErrorMessage ?? "Chyba při stahování z ARES",
                                            },
                                        })
                                    );
                                })
                            }}
                        />
                    </Paper>
                </Grid>

                <Grid item md={4}>
                    <Paper className={classes.section}>
                        <Typography variant={"caption"}>Kontaktní údaje</Typography>
                        <CrudDetailAutoFields<OfferResponse>
                            form={form as any}
                            itemFields={itemFields}
                            submitting={submitting}
                            includeFields={["telephone", "email", "website"]}
                        />
                    </Paper>
                </Grid>

                <Grid item md={4}>
                    <Paper className={classes.section}>
                        <Typography variant={"caption"}>Fakturace</Typography>
                        <CrudDetailAutoFields<OfferResponse>
                            form={form as any}
                            itemFields={itemFields}
                            includeFields={["invoicingEmail"]}
                            submitting={submitting}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <CrudDetailAutoFields<OfferResponse>
                                    form={form as any}
                                    itemFields={itemFields}
                                    includeFields={["invoiceDue"]}
                                    submitting={submitting}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <InputSelect
                                    field={{name: "currencyID", title: "Měna"}}
                                    itemsGetter={async (search) => {
                                        const currencies: CurrencyResponse[] = await getAllCurrencies();
                                        return currencies.map(c => (
                                            <MenuItem key={`mi-c-${c.currencyID}`} value={c.currencyID}>{c.alpha3Code}</MenuItem>
                                        ))
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <CrudDetailAutoFields<OfferResponse>
                            form={form as any}
                            itemFields={itemFields}
                            includeFields={["transferredVAT"]}
                            submitting={submitting}
                        />
                    </Paper>
                </Grid>

                {/* Střední řada - 2 sloupce */}
                <Grid item md={8}>
                    <Paper className={classes.section}>
                        <Typography variant={"caption"}>Adresa</Typography>
                        <CrudDetailAutoFields form={form} itemFields={[
                            {
                                name: "address.line1",
                                title: "Řádek 1",
                                description: "Řádek 1",
                            },
                            {
                                name: "address.line2",
                                title: "Řádek 2",
                                description: "Řádek 2",
                            }
                        ]} submitting={submitting} />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <CrudDetailAutoFields form={form} itemFields={[
                                    {
                                        name: "address.city",
                                        title: "Město",
                                        description: "Město",
                                    }
                                ]} submitting={submitting} />
                            </Grid>
                            <Grid item xs={6}>
                                <CrudDetailAutoFields form={form} itemFields={[
                                    {
                                        name: "address.zipCode",
                                        title: "PSČ",
                                        description: "PSČ",
                                    }
                                ]} submitting={submitting} />
                            </Grid>
                        </Grid>
                        <CrudDetailAutoFields form={form} itemFields={[
                            {
                                name: "address.country",
                                title: "Země",
                                description: "Země",
                            }
                        ]} submitting={submitting} />
                    </Paper>
                </Grid>

                <Grid item md={4}>
                    <Paper className={classes.section}>
                        <Typography variant={"caption"}>Doprava</Typography>
                        <CrudDetailAutoFields form={form} itemFields={[
                            {
                                name: "distanceKm",
                                title: "Vzdálenost v km",
                                description: "Vzdálenost v kilometrech",
                                formFieldDef: {
                                    type: "number"
                                }
                            },
                            {
                                name: "distanceH",
                                title: "Vzdálenost v h",
                                description: "Vzdálenost v hodinách",
                                formFieldDef: {
                                    type: "number"
                                }
                            }
                        ]} submitting={submitting} />
                    </Paper>
                </Grid>

                {/* Dolní řada - poznámky */}
                <Grid item md={12}>
                    <Paper className={classes.section}>
                        <Typography variant={"caption"}>Poznámky</Typography>
                        <CrudDetailAutoFields<OfferResponse>
                            form={form as any}
                            itemFields={itemFields}
                            submitting={submitting}
                            includeFields={["description"]}
                        />
                    </Paper>
                </Grid>

                {/* Kontakty */}
                <Grid item md={12}>
                    <Paper className={classes.section}>
                        <Typography variant={"caption"}>Konktakty</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <CompanyContactsFormPart field={{name: "contactPersons", title: "Konktaktní osoby"}} form={form} />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>






        </>
    );
}
export default CompanyForm;